/* ==========================================================================
   Vithoba — Products Landing Page
   No dependencies. Native scrolling, IntersectionObserver, Pointer Events.
   Survives Shopify Theme Editor section reloads.
   ========================================================================== */

(function () {
  'use strict';

  var reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var prefersReducedMotion = function () {
    return reduceMotionQuery.matches;
  };

  var isDesignMode = function () {
    return Boolean(window.Shopify && window.Shopify.designMode);
  };

  /* ------------------------------------------------------------------------
     Small helpers
     ------------------------------------------------------------------------ */

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  /* ------------------------------------------------------------------------
     Reveal animations
     ------------------------------------------------------------------------ */

  var revealObserver = null;

  function ensureRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;

    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    return revealObserver;
  }

  function initReveals(root) {
    var scope = root || document;
    var targets = scope.querySelectorAll('[data-vlp-reveal]');
    if (!targets.length) return;

    // Without IntersectionObserver or with reduced motion, content just stays visible.
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    // The body class is what actually hides the elements, so it is only added
    // once we know we can reveal them again. No JS, no hidden content.
    document.body.classList.add('vlp-reveal-ready');

    var observer = ensureRevealObserver();

    targets.forEach(function (el) {
      if (el.classList.contains('is-revealed')) return;

      // Anything already in view on load reveals immediately rather than
      // waiting for a scroll — the hero must never feel delayed.
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.92) {
        requestAnimationFrame(function () {
          el.classList.add('is-revealed');
        });
        return;
      }

      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Smooth in-page scrolling
     ------------------------------------------------------------------------ */

    function onAnchorClick(event) {
    var link = event.target.closest('[data-vlp-scroll]');
    if (!link) return;

    var href = link.getAttribute('href') || '';
    var hashIndex = href.indexOf('#');
    if (hashIndex === -1) return;

    var id = href.slice(hashIndex + 1);
    if (!id) return;

    // Only intercept links that point at this page.
    var path = href.slice(0, hashIndex);
    if (path && path !== window.location.pathname && path.indexOf('#') !== 0) return;

    var target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();

    // Offset for the sticky header's real, current height so the target
    // never lands hidden underneath it.
    var header = document.querySelector('[data-vithoba-header]');
    var headerHeight = header ? header.offsetHeight : 0;
    var targetTop = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;

    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    });

    // Move focus so keyboard and screen-reader users follow the jump.
    var hadTabIndex = target.hasAttribute('tabindex');
    if (!hadTabIndex) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    if (!hadTabIndex) {
      target.addEventListener(
        'blur',
        function () {
          target.removeAttribute('tabindex');
        },
        { once: true }
      );
    }

    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', '#' + id);
    }
  }

  function initSmoothScrolling() {
    if (document.body.dataset.vlpScrollBound === 'true') return;
    document.body.dataset.vlpScrollBound = 'true';
    document.addEventListener('click', onAnchorClick);
  }

  /* ------------------------------------------------------------------------
     Hero
     ------------------------------------------------------------------------ */

  function VithobaHero(el) {
    this.el = el;
    this.image = el.querySelector('.vithoba-lp-hero__image');
    this.enabled =
      el.dataset.parallax === 'true' &&
      this.image &&
      !prefersReducedMotion() &&
      window.matchMedia('(min-width: 1100px) and (hover: hover) and (pointer: fine)').matches;

    this.ticking = false;
    this.visible = false;

    if (!this.enabled) return;

    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);

    if ('IntersectionObserver' in window) {
      var self = this;
      this.io = new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
      });
      this.io.observe(this.el);
    } else {
      this.visible = true;
    }

    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
  }

  VithobaHero.prototype.onScroll = function () {
    if (this.ticking || !this.visible) return;
    this.ticking = true;
    requestAnimationFrame(this.update);
  };

  VithobaHero.prototype.update = function () {
    this.ticking = false;
    var rect = this.el.getBoundingClientRect();
    // Deliberately tiny: a maximum of 24px of drift across the whole hero.
    var progress = clamp(-rect.top / (rect.height || 1), 0, 1);
    this.image.style.setProperty('--vlp-parallax', (progress * 24).toFixed(2) + 'px');
  };

  VithobaHero.prototype.destroy = function () {
    if (!this.enabled) return;
    window.removeEventListener('scroll', this.onScroll);
    if (this.io) this.io.disconnect();
  };

  /* ------------------------------------------------------------------------
     Slider
     ------------------------------------------------------------------------ */

  function VithobaSlider(el) {
    this.el = el;
    this.track = el.querySelector('[data-vlp-track]');
    this.viewport = el.querySelector('[data-vlp-viewport]');
    this.slides = Array.prototype.slice.call(el.querySelectorAll('[data-vlp-slide]'));
    this.prevButton = el.querySelector('[data-vlp-prev]');
    this.nextButton = el.querySelector('[data-vlp-next]');
    this.progressBar = el.querySelector('[data-vlp-progress-bar]');
    this.pagination = el.querySelector('[data-vlp-pagination]');

    if (!this.track || !this.slides.length) return;

    this.settings = {
      autoplay: el.dataset.autoplay === 'true',
      autoplayDelay: parseInt(el.dataset.autoplayDelay, 10) || 4500,
      speed: parseInt(el.dataset.speed, 10) || 620,
      pauseOnHover: el.dataset.pauseOnHover === 'true',
      returnToStart: el.dataset.returnToStart === 'true',
      drag: el.dataset.drag === 'true',
      swipe: el.dataset.swipe === 'true'
    };

    this.animation = null;
    this.autoplayTimer = null;
    this.resumeTimer = null;
    this.paused = false;
    this.interacted = false;
    this.dots = [];

    this.bind();
    this.buildPagination();
    this.refresh();
    this.startAutoplay();
  }

  /* ---- geometry ---- */

  VithobaSlider.prototype.step = function () {
    if (this.slides.length < 2) return this.track.clientWidth;
    // Distance between two adjacent slide starts, including the gap.
    var delta = this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    return delta > 0 ? delta : this.slides[0].offsetWidth;
  };

  VithobaSlider.prototype.maxScroll = function () {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  };

  VithobaSlider.prototype.atStart = function () {
    return this.track.scrollLeft <= 2;
  };

  VithobaSlider.prototype.atEnd = function () {
    return this.track.scrollLeft >= this.maxScroll() - 2;
  };

  /* ---- animated scrolling, so the merchant's speed setting is real ---- */

  VithobaSlider.prototype.scrollTo = function (target, immediate) {
    var self = this;
    var max = this.maxScroll();
    var to = clamp(target, 0, max);
    var from = this.track.scrollLeft;
    var distance = to - from;

    this.cancelAnimation();

    if (immediate || prefersReducedMotion() || Math.abs(distance) < 1) {
      this.track.scrollLeft = to;
      this.refresh();
      return;
    }

    // Scroll-snap fights a programmatic, eased scroll: on every intermediate
    // scrollLeft write, the browser tries to yank the position to the nearest
    // snap-aligned card instead of letting it glide, which is what produced
    // the "jumps instead of glides" behaviour. Suspend snapping only for the
    // duration of this animation, then hand control back to CSS once settled.
    this.track.style.scrollSnapType = 'none';

    var duration = this.settings.speed;
    var start = null;

    function frame(timestamp) {
      if (start === null) start = timestamp;
      var elapsed = timestamp - start;
      var progress = clamp(elapsed / duration, 0, 1);
      self.track.scrollLeft = from + distance * easeInOutCubic(progress);

      if (progress < 1) {
        self.animation = requestAnimationFrame(frame);
      } else {
        self.animation = null;
        self.track.style.scrollSnapType = '';
        self.refresh();
      }
    }

    this.animation = requestAnimationFrame(frame);
  };

  VithobaSlider.prototype.cancelAnimation = function () {
    if (this.animation) {
      cancelAnimationFrame(this.animation);
      this.animation = null;
    }
  };

  VithobaSlider.prototype.next = function (fromAutoplay) {
    if (this.atEnd()) {
      // No cloned slides, no duplicate IDs, no fake infinity: at the end we
      // simply glide back to the first product when the merchant allows it.
      if (fromAutoplay && this.settings.returnToStart) {
        this.scrollTo(0);
      }
      return;
    }
    this.scrollTo(this.track.scrollLeft + this.step());
  };

  VithobaSlider.prototype.prev = function () {
    this.scrollTo(this.track.scrollLeft - this.step());
  };

  /* ---- state ---- */

  VithobaSlider.prototype.refresh = function () {
    var max = this.maxScroll();
    var ratio = max > 0 ? this.track.scrollLeft / max : 1;

    if (this.progressBar) {
      // The bar represents the visible portion, then slides as you scroll.
      var visible = max > 0 ? this.track.clientWidth / this.track.scrollWidth : 1;
      this.progressBar.style.setProperty('--vlp-progress', Math.max(visible, 0.08).toFixed(3));
      this.progressBar.style.transform =
        'translateX(' + (ratio * (1 - Math.max(visible, 0.08)) * 100).toFixed(2) + '%) ' +
        'scaleX(' + Math.max(visible, 0.08).toFixed(3) + ')';
    }

    if (this.prevButton) this.prevButton.disabled = this.atStart();
    if (this.nextButton) {
      this.nextButton.disabled = this.atEnd() && !(this.settings.autoplay && this.settings.returnToStart);
    }

    if (this.dots.length) {
      var pages = this.dots.length;
      var active = max > 0 ? Math.round(ratio * (pages - 1)) : 0;
      this.dots.forEach(function (dot, i) {
        var isActive = i === active;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        dot.tabIndex = isActive ? 0 : -1;
      });
    }

    // Slides scrolled out of view should not be keyboard-reachable traps,
    // but they must stay in the accessibility tree, so nothing is hidden here.
  };

  VithobaSlider.prototype.buildPagination = function () {
    if (!this.pagination) return;

    var self = this;
    var perView = this.track.clientWidth / (this.step() || 1);
    var pages = Math.max(1, Math.ceil(this.slides.length - perView + 1));
    pages = Math.min(pages, this.slides.length);

    this.pagination.innerHTML = '';
    this.dots = [];

    for (var i = 0; i < pages; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'vithoba-lp-slider__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', 'Go to product ' + (i + 1));
      dot.dataset.index = String(i);
      dot.addEventListener('click', function (event) {
        var index = parseInt(event.currentTarget.dataset.index, 10);
        self.markInteracted();
        self.scrollTo(index * self.step());
      });
      this.pagination.appendChild(dot);
      this.dots.push(dot);
    }
  };

  /* ---- autoplay ---- */

  VithobaSlider.prototype.startAutoplay = function () {
    if (!this.settings.autoplay || prefersReducedMotion() || this.slides.length < 2) return;
    if (this.maxScroll() <= 0) return;

    this.stopAutoplay();

    var self = this;
    this.autoplayTimer = window.setInterval(function () {
      if (self.paused || document.hidden) return;
      self.next(true);
    }, this.settings.autoplayDelay);
  };

  VithobaSlider.prototype.stopAutoplay = function () {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  };

  VithobaSlider.prototype.pause = function () {
    this.paused = true;
  };

  VithobaSlider.prototype.resume = function () {
    this.paused = false;
  };

  VithobaSlider.prototype.markInteracted = function () {
    // A deliberate interaction pauses autoplay, then hands control back after
    // a courteous gap rather than fighting the visitor mid-swipe.
    var self = this;
    this.interacted = true;
    this.pause();

    if (this.resumeTimer) window.clearTimeout(this.resumeTimer);
    this.resumeTimer = window.setTimeout(function () {
      self.resume();
    }, Math.max(this.settings.autoplayDelay, 5000));
  };

  /* ---- events ---- */

  VithobaSlider.prototype.bind = function () {
    var self = this;
    this.listeners = [];

    function on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      self.listeners.push([target, type, handler, options]);
    }

    /* Native scroll drives every visual state. */
    var scrollTicking = false;
    on(
      this.track,
      'scroll',
      function () {
        if (scrollTicking) return;
        scrollTicking = true;
        requestAnimationFrame(function () {
          scrollTicking = false;
          self.refresh();
        });
      },
      { passive: true }
    );

    if (this.prevButton) {
      on(this.prevButton, 'click', function () {
        self.markInteracted();
        self.prev();
      });
    }

    if (this.nextButton) {
      on(this.nextButton, 'click', function () {
        self.markInteracted();
        self.next(false);
      });
    }

    if (this.settings.pauseOnHover) {
      on(this.el, 'mouseenter', function () {
        self.pause();
      });
      on(this.el, 'mouseleave', function () {
        if (!self.interacted) self.resume();
      });
    }

    /* Keyboard focus inside the slider pauses autoplay. */
    on(this.el, 'focusin', function () {
      self.pause();
    });
    on(this.el, 'focusout', function (event) {
      if (!self.el.contains(event.relatedTarget)) self.resume();
    });

    /* Arrow keys on the viewport. */
    on(this.viewport || this.el, 'keydown', function (event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        self.markInteracted();
        self.next(false);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        self.markInteracted();
        self.prev();
      }
    });

    /* Touch: the browser handles the swipe natively. We only pause autoplay,
       which is why vertical page scrolling on mobile is never hijacked. */
    on(
      this.track,
      'touchstart',
      function () {
        if (self.settings.swipe) self.markInteracted();
      },
      { passive: true }
    );

    /* Mouse drag on desktop, via Pointer Events. */
    if (this.settings.drag) this.bindDrag(on);

    /* Recompute on resize. */
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(function () {
        self.buildPagination();
        self.refresh();
      });
      this.resizeObserver.observe(this.track);
    } else {
      on(window, 'resize', function () {
        self.buildPagination();
        self.refresh();
      });
    }
  };

  VithobaSlider.prototype.bindDrag = function (on) {
    var self = this;
    var pointerId = null;
    var startX = 0;
    var startScroll = 0;
    var moved = 0;
    var DRAG_THRESHOLD = 6;

    on(this.track, 'pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = self.track.scrollLeft;
      moved = 0;
      self.cancelAnimation();
      self.markInteracted();
    });

    on(this.track, 'pointermove', function (event) {
      if (pointerId === null || event.pointerId !== pointerId) return;

      var delta = event.clientX - startX;
      if (Math.abs(delta) < DRAG_THRESHOLD && moved === 0) return;

      // Only now do we commit to a drag, so a plain click still opens the product.
      if (moved === 0) {
        self.el.classList.add('is-dragging');
        try {
          self.track.setPointerCapture(pointerId);
        } catch (e) {
          /* capture is a nicety, not a requirement */
        }
      }

      moved = Math.abs(delta);
      event.preventDefault();
      self.track.scrollLeft = startScroll - delta;
    });

    function endDrag(event) {
      if (pointerId === null || (event && event.pointerId !== pointerId)) return;

      try {
        self.track.releasePointerCapture(pointerId);
      } catch (e) {
        /* nothing to release */
      }

      pointerId = null;

      if (moved > DRAG_THRESHOLD) {
        // Settle onto the nearest slide so we never stop mid-card.
        var step = self.step();
        var index = Math.round(self.track.scrollLeft / step);
        self.scrollTo(index * step);

        // Keep links inert until after this click event has passed.
        window.setTimeout(function () {
          self.el.classList.remove('is-dragging');
        }, 0);
      } else {
        self.el.classList.remove('is-dragging');
      }

      moved = 0;
    }

    on(this.track, 'pointerup', endDrag);
    on(this.track, 'pointercancel', endDrag);
    on(this.track, 'pointerleave', endDrag);

    // Images must not be draggable or the browser starts a native image drag.
    on(this.track, 'dragstart', function (event) {
      if (event.target.tagName === 'IMG') event.preventDefault();
    });
  };

  VithobaSlider.prototype.destroy = function () {
    this.stopAutoplay();
    this.cancelAnimation();
    if (this.resumeTimer) window.clearTimeout(this.resumeTimer);
    if (this.resizeObserver) this.resizeObserver.disconnect();

    (this.listeners || []).forEach(function (entry) {
      entry[0].removeEventListener(entry[1], entry[2], entry[3]);
    });
    this.listeners = [];
  };

  /* ------------------------------------------------------------------------
     Quick add
     ------------------------------------------------------------------------ */

  var toastTimer = null;

  function showToast(message, isError) {
    var toast = document.getElementById('vithoba-lp-toast');
    if (!toast) return;

    var text = toast.querySelector('.vithoba-lp-toast__text');
    var link = toast.querySelector('.vithoba-lp-toast__link');
    if (text) text.textContent = message;
    if (link) link.hidden = Boolean(isError);

    toast.hidden = false;
    requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });

    if (toastTimer) window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      toast.classList.remove('is-visible');
      window.setTimeout(function () {
        toast.hidden = true;
      }, 300);
    }, 4000);
  }

  function findThemeCartDrawer() {
    // Reuse the theme's own cart drawer when the layout renders it.
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function openThemeCartDrawer(drawer, sections) {
    try {
      if (typeof drawer.renderContents === 'function') {
        drawer.renderContents(sections);
        return true;
      }
      if (typeof drawer.open === 'function') {
        drawer.open();
        return true;
      }
    } catch (e) {
      /* fall through to the toast */
    }
    return false;
  }

  function onQuickAddSubmit(event) {
    var form = event.target.closest('[data-vlp-quick-add]');
    if (!form) return;

    event.preventDefault();

    var button = form.querySelector('[data-vlp-quick-add-button]');
    var routes = (window.VithobaLP && window.VithobaLP.routes) || {};
    var strings = (window.VithobaLP && window.VithobaLP.strings) || {};
    var addUrl = routes.cart_add_url || '/cart/add';

    if (button) {
      button.classList.add('is-loading');
      button.disabled = true;
    }

    var body = new FormData(form);
    var drawer = findThemeCartDrawer();

    // If the theme's drawer is present, ask Shopify to re-render its section
    // in the same request so the drawer updates without a second round trip.
    if (drawer && drawer.getSectionsToRender) {
      try {
        body.append(
          'sections',
          drawer
            .getSectionsToRender()
            .map(function (section) {
              return section.id;
            })
            .join(',')
        );
        body.append('sections_url', window.location.pathname);
      } catch (e) {
        /* optional optimisation only */
      }
    }

    fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data.status) {
          showToast(data.description || data.message || strings.addToCartError || 'Something went wrong.', true);
          return;
        }

        document.dispatchEvent(new CustomEvent('vithoba-lp:added-to-cart', { detail: data }));

        if (!(drawer && openThemeCartDrawer(drawer, data.sections))) {
          showToast(strings.addedToCart || 'Added to cart');
        }

        updateCartCount();
      })
      .catch(function () {
        showToast(strings.addToCartError || "Couldn't add that item. Try again.", true);
      })
      .finally(function () {
        if (button) {
          button.classList.remove('is-loading');
          button.disabled = false;
        }
      });
  }

  function updateCartCount() {
    // Best-effort: keep any theme cart bubble in sync if one exists.
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        document.querySelectorAll('.cart-count-bubble span:first-child').forEach(function (el) {
          el.textContent = cart.item_count;
        });
      })
      .catch(function () {
        /* purely cosmetic */
      });
  }

  function initQuickAdd() {
    if (document.body.dataset.vlpCartBound === 'true') return;
    document.body.dataset.vlpCartBound = 'true';
    document.addEventListener('submit', onQuickAddSubmit);
  }

  /* ------------------------------------------------------------------------
     Registry and lifecycle
     ------------------------------------------------------------------------ */

  var sliders = new Map();
  var heroes = new Map();

  function initSliders(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-vithoba-slider]').forEach(function (el) {
      var id = el.dataset.vithobaSlider;
      if (el.dataset.enabled !== 'true') return;
      // Never initialise the same section twice.
      if (sliders.has(id)) return;
      sliders.set(id, new VithobaSlider(el));
    });
  }

  function initHeroes(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-vithoba-hero]').forEach(function (el) {
      var id = el.dataset.vithobaHero;
      if (heroes.has(id)) return;
      heroes.set(id, new VithobaHero(el));
    });
  }

  function destroySection(id) {
    if (sliders.has(id)) {
      sliders.get(id).destroy();
      sliders.delete(id);
    }
    if (heroes.has(id)) {
      heroes.get(id).destroy();
      heroes.delete(id);
    }
  }

  function initLandingPage(root) {
    initSmoothScrolling();
    initQuickAdd();
    initHeroes(root);
    initSliders(root);
    initReveals(root);
  }

  /* Shopify Theme Editor: sections are swapped in and out without a reload. */
  document.addEventListener('shopify:section:load', function (event) {
    var id = event.detail.sectionId;
    destroySection(id);
    initLandingPage(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroySection(event.detail.sectionId);
  });

  document.addEventListener('shopify:section:select', function (event) {
    var slider = sliders.get(event.detail.sectionId);
    if (slider) slider.pause();
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    var slider = sliders.get(event.detail.sectionId);
    if (slider && !isDesignMode()) slider.resume();
  });

  /* Pause every autoplay while the tab is hidden. */
  document.addEventListener('visibilitychange', function () {
    sliders.forEach(function (slider) {
      if (document.hidden) {
        slider.pause();
      } else if (!slider.interacted) {
        slider.resume();
      }
    });
  });

  /* Reduced-motion can be toggled mid-session. */
  var onMotionChange = function () {
    sliders.forEach(function (slider) {
      if (prefersReducedMotion()) {
        slider.stopAutoplay();
      } else {
        slider.startAutoplay();
      }
    });
  };

  if (typeof reduceMotionQuery.addEventListener === 'function') {
    reduceMotionQuery.addEventListener('change', onMotionChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initLandingPage();
    });
  } else {
    initLandingPage();
  }

  // Exposed for debugging and for anyone extending the page later.
  window.VithobaLP = window.VithobaLP || {};
  window.VithobaLP.sliders = sliders;
  window.VithobaLP.init = initLandingPage;
})();